import React, { PropsWithChildren, createContext, useContext, useState } from 'react'
import useBalances from '../hooks/useBalances'
import useConfig from '../hooks/useConfig'
import { PlugToken } from '../interfaces/common'
import { SwapProviderQuote, SwapProviderQuoteFetchError } from '../interfaces/swapInterface'
import { filterDestinationTokens } from '../utils/filterDestinationTokens'

interface ISwapProvider {
  fromToken?: PlugToken
  setFromToken: (token?: PlugToken) => void
  toToken?: PlugToken
  setToToken: (token?: PlugToken) => void
  quotes: SwapProviderQuote[]
  setQuotes: (quotes: SwapProviderQuote[]) => void
  quoteFetchErrors: SwapProviderQuoteFetchError[]
  setQuoteFetchErrors: (quotes: SwapProviderQuoteFetchError[]) => void
  selectedProvider?: string
  setSelectedProvider: (provider?: string) => void
  manualProvider?: string
  setManualProvider: (provider: string) => void
  amount: string
  setAmount: (newAmmount: string) => void
  toAmount: string
  setToAmount: (newAmount: string) => void
  swapReviewVisible: boolean
  setSwapReviewVisible: (visible: boolean) => void
  swapConfigVisible: boolean
  setSwapConfigVisible: (visible: boolean) => void
  swapLoading: boolean
  setSwapLoading: (loading: boolean) => void
  lockedToToken: boolean
  setLockedToToken: (locked: boolean) => void
}

const SwapContext = createContext<ISwapProvider>(null as any)

export const SwapProvider = ({ children }: PropsWithChildren) => {
  const [amount, setAmount] = useState<string>('')
  const [fromToken, setFromToken] = useState<PlugToken>()
  const [toToken, setToToken] = useState<PlugToken>()
  const [quotes, setQuotes] = useState<SwapProviderQuote[]>([])
  const [quoteFetchErrors, setQuoteFetchErrors] = useState<SwapProviderQuoteFetchError[]>([])
  const [selectedProvider, setSelectedProvider] = useState<string>()
  const [manualProvider, setManualProvider] = useState<string>()
  const [toAmount, setToAmount] = useState<string>('')
  const [swapReviewVisible, setSwapReviewVisible] = useState(false)
  const [swapConfigVisible, setSwapConfigVisible] = useState(false)
  const [swapLoading, setSwapLoading] = useState(false)
  const [lockedToToken, setLockedToToken] = useState(false)

  const { refreshBalance } = useBalances()
  const { tokens, evmChains } = useConfig()

  const onSetFromToken = (token: PlugToken | undefined) => {
    setQuotes([])
    setFromToken(token)

    if (token) refreshBalance(token)

    // A locked destination (e.g. funding the perp account) stays fixed regardless
    // of the chosen source.
    if (lockedToToken) return

    if (!toToken || toToken?.chain !== token?.chain || toToken.id === token.id) {
      const destTokens = filterDestinationTokens(token, tokens, evmChains)

      let tToken = destTokens.find((t) => t.chain === token?.chain && t.id !== token?.id)
      if (!tToken) {
        tToken = destTokens.find((t) => t.id !== token?.id)
      }

      setToToken(tokens.find((t) => t.id === tToken?.id))
    }
  }

  const onSetToToken = (token: PlugToken | undefined) => {
    setQuotes([])
    setToToken(token)
  }

  return (
    <SwapContext.Provider
      value={{
        amount,
        setAmount,
        fromToken,
        setFromToken: onSetFromToken,
        toToken,
        setToToken: onSetToToken,
        quotes,
        setQuotes,
        quoteFetchErrors,
        setQuoteFetchErrors,
        selectedProvider,
        setSelectedProvider,
        toAmount,
        setToAmount,
        manualProvider,
        setManualProvider,
        swapReviewVisible,
        setSwapReviewVisible,
        swapConfigVisible,
        setSwapConfigVisible,
        swapLoading,
        setSwapLoading,
        lockedToToken,
        setLockedToToken,
      }}
    >
      {children}
    </SwapContext.Provider>
  )
}

export const useSwapContext = () => useContext(SwapContext)
